
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import FAQSection from './components/FAQSection';

export default function FAQPage() {
  const generalQuestions = [
    {
      question: '1. What is CrowdRounds?',
      answer: 'CrowdRounds is an advanced evaluation system that uses intelligence frameworks to score, analyze, and predict app performance.',
    },
    {
      question: '2. Who is this for?',
      answer: 'Founders at any stage — idea, prototype, launched, or scaling.',
    },
    {
      question: '3. Do you use human testers?',
      answer: 'No. We use structured intelligence systems for consistency, fairness, and precision.',
    },
    {
      question: '4. What makes CrowdRounds different?',
      answer: 'Our multi-system approach, global benchmarks, emotional and cognitive analysis, and no guesswork.',
    },
    {
      question: '5. Do you help ideas or only existing apps?',
      answer: 'Both. We have tiers designed for each stage.',
    },
  ];

  const tierQuestions = [
    {
      question: '6. What tier should I choose?',
      answer: 'Choose based on how much clarity you need. Tier 1 is quick. Tier 5 is complete.',
    },
    {
      question: '7. Can I upgrade later?',
      answer: 'Yes. Every report includes 1 suggested next step with a direct upgrade link.',
    },
    {
      question: '8. How long does each tier take?',
      answer: 'Between 24 hours and 7 days depending on the tier.',
    },
    {
      question: '9. What\'s the difference between Tier 3 and Tier 4?',
      answer: 'Tier 3 shows your competitive position. Tier 4 gives you a growth blueprint.',
    },
    {
      question: '10. Does Tier 5 include everything?',
      answer: 'Yes — including 7 intelligence systems, global indicators, and investor readiness.',
    },
  ];

  const scoringQuestions = [
    {
      question: '11. What is the App Credit Score™?',
      answer: 'A structured, multi-factor score that measures your app\'s performance potential.',
    },
    {
      question: '12. What is the Validation Passport™?',
      answer: 'Your official readiness certification.',
    },
    {
      question: '13. What are the intelligence systems?',
      answer: 'Seven proprietary systems measuring cognitive load, emotional flow, category benchmarks, and more.',
    },
    {
      question: '14. How is my score calculated?',
      answer: 'Through structured logic and benchmark comparisons — no human interference.',
    },
    {
      question: '15. Are the scores harsh?',
      answer: 'Yes — intentionally. Strong apps prove themselves. Weak areas get exposed clearly.',
    },
  ];

  const reportQuestions = [
    {
      question: '16. How is my report delivered?',
      answer: 'A polished PDF emailed directly to you.',
    },
    {
      question: '17. Will my report include recommendations?',
      answer: 'Yes — each tier includes 1 to 3 high-impact next steps.',
    },
    {
      question: '18. Will you rewrite my app?',
      answer: 'No. We evaluate, clarify, and direct — we do not rebuild your app.',
    },
    {
      question: '19. What if something is missing?',
      answer: 'You will receive a request for additional information if needed.',
    },
    {
      question: '20. Can I get a call with someone?',
      answer: 'Only included with Tier 5 (optional).',
    },
  ];

  const globalQuestions = [
    {
      question: '21. What is Global Indicators?',
      answer: 'A system that maps your app to countries with the highest success potential.',
    },
    {
      question: '22. Can you help me get investors?',
      answer: 'We provide readiness scoring — not investment brokerage.',
    },
    {
      question: '23. Do you connect founders with buyers?',
      answer: 'Only through separate high-tier add-ons.',
    },
    {
      question: '24. Can you guarantee downloads?',
      answer: 'Never. We provide insight, not outcomes.',
    },
    {
      question: '25. What if I only want global expansion?',
      answer: 'You can purchase the Global Visibility Boost add-on.',
    },
  ];

  const techQuestions = [
    {
      question: '26. Is my data kept confidential?',
      answer: 'Yes — always.',
    },
    {
      question: '27. Do you store my app data?',
      answer: 'Only long enough to complete your evaluation.',
    },
    {
      question: '28. Do you sell data to third parties?',
      answer: 'No.',
    },
    {
      question: '29. Is your scoring system automated?',
      answer: 'Yes — structured, automated, and bias-free.',
    },
    {
      question: '30. What if I have more questions?',
      answer: 'Email support@crowdrounds.org.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-blue-600 to-indigo-700 text-white py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <i className="ri-question-line text-4xl"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            Everything you need to know about CrowdRounds, our tiers, scoring systems, and evaluation process.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQSection title="Section A — General Questions" faqs={generalQuestions} />
          <FAQSection title="Section B — Tier Questions" faqs={tierQuestions} />
          <FAQSection title="Section C — Scoring & Intelligence" faqs={scoringQuestions} />
          <FAQSection title="Section D — Report & Delivery" faqs={reportQuestions} />
          <FAQSection title="Section E — Global Expansion & Investors" faqs={globalQuestions} />
          <FAQSection title="Section F — Tech & Security" faqs={techQuestions} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Our team is here to help you understand how CrowdRounds can provide clarity for your app.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@crowdrounds.org"
              className="px-8 py-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-semibold rounded-lg hover:from-teal-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap inline-flex items-center justify-center gap-2"
            >
              <i className="ri-mail-line text-xl"></i>
              Email Support
            </a>
            <a
              href="/pricing"
              className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              View Pricing
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
